import { useABAC } from '@adornis/abac/client/useABAC.js';
import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { MapBox } from '@adornis/maps/client/index.js';
import type { SNEtaWindow } from '@nsc/servicenow/types.js';
import { html, nothing } from 'lit';
import {
  getFEGeolocationForTicket,
  stopMyGeolocationTracking,
  updateMyGeolocation,
} from '../../api/geolocation-queries.js';
import {
  ticketActionAccessNotGranted,
  ticketActionAddComment,
  ticketActionAttemptedContact,
  ticketActionCancellationRequested,
  ticketActionCustomerRequestedReschedule,
  ticketActionEnRoute,
  ticketActionEtaWindow,
  ticketActionInterventionAccepted,
  ticketActionInterventionFinished,
  ticketActionInterventionStarted,
  ticketActionOtherProblem,
  ticketActionPartsClaimed,
  ticketActionPartsNotDispatched,
  ticketActionPartsReturnedToPudo,
  ticketActionUnsuccessfulVisit,
} from '../../api/ticket-action-queries.js';
import { getTicketByNumber, subscribeTicketChanges } from '../../api/ticket-queries.js';
import { getMyTicketReview, submitTicketReview } from '../../api/ticket-review-queries.js';
import { getMyAppUser } from '../../api/user-queries.js';
import { config } from '../../config.js';
import { accessController } from '../../features/permissions/init-permissions.js';
import { SmartTeamsAppPermission, SmartTeamsAppUserGroup } from '../../features/permissions/roles-and-permissions.js';
import * as m from '../../localization/out/messages.js';
import { router } from '../startup.js';
import {
  CadModal,
  CancellationModal,
  CnaModal,
  CommentModal,
  CraModal,
  CusModal,
  TpmModal,
  TravelStartModal,
  WorkflowTimestampModal,
  type CadModalValues,
} from './_action-modals.js';
import { windowDate, windowLabel } from './_colleague-format.js';
import { ETA_WINDOW_KEYS, etaWindowLabel } from './_eta-window.js';
import { notesBlock } from './_notes-block.js';
import { addDrivingRouteWhenReady } from './_route-helpers.js';
import { STATE_BADGE_CLASS } from './_state-badge.js';

const errorOrFallback = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

const GEO_UPDATE_INTERVAL = 30_000;
let lastGeoUpdate = 0;

// SVR (Service Visit Report) is out of scope for v1 — the page and route stay in place but
// the entry point is hidden. Flip back to `true` to re-enable once the backend exists.
export const SVR_ENABLED = false;

/** Format a Date as `yyyy-mm-dd hh:mm:ss` GMT — the format the SN spec requires. */
function toGmtTimestamp(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(
    date.getUTCHours(),
  )}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
}

/** Local HH:mm for a stored ISO timestamp (the FE's last position update), or '' if unparseable. */
function toLocalTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Title-case an SN ALL-CAPS string (status/commodity) for display, e.g. `CABLES AND CORDS` → `Cables And Cords`. */
function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Fulfilment badge for a part requirement, derived from the new sourced/delivered/stage signals
 * (SN dropped the old logistics `status`). Reuses existing localized status labels so no new
 * strings are needed; a free-form `stage` falls back to its title-cased SN value on a neutral badge.
 */
function partFulfilmentInfo(part: {
  delivered?: boolean | null;
  sourced?: boolean | null;
  stage?: string | null;
}): { label: string; badgeClass: string } {
  if (part.delivered) return { label: m.part_status_delivered(), badgeClass: 'badge-success' };
  if (part.sourced) return { label: m.part_status_ordered(), badgeClass: 'badge-info' };
  if (part.stage?.trim()) return { label: titleCase(part.stage), badgeClass: 'badge-neutral' };
  return { label: '', badgeClass: 'badge-neutral' };
}

/**
 * Google Maps link for an address — prefers coordinates, falls back to the address text. The
 * `?api=1&query=` universal URL opens the native Maps app on mobile (and maps.google.com on
 * desktop). Used for the site location and the PUDO depot.
 */
function mapsHref(label: string | null | undefined, lat: number | null | undefined, lng: number | null | undefined): string | null {
  if (lat != null && lng != null) return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  if (label) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`;
  return null;
}

// Workflow buttons for a WOT (state, substate). On-Route lives in the substate, not the state:
// `en_route` keeps the WOT at 17 (Accepted) and flips substate ∅ → 8 (spec §4.3/§6.3).
type WorkflowKey = 'accept' | 'travel_start' | 'intervention_start' | 'intervention_end';

interface WorkflowState {
  enabled: Record<WorkflowKey, boolean>;
  next: WorkflowKey | null;
}

/** WOT substate 8 = On Route (spec §6.3), set by the FE's `en_route`/Travel Start action. */
const SUBSTATE_ON_ROUTE = '8';

/**
 * Master switch for the FE parts-handling actions (Claim parts / Leave at PUDO / Parts not found).
 * Currently OFF: the whole section is hidden on the ticket detail page. The read-only parts manifest
 * and the overview part numbers are unaffected. Flip to true to bring the buttons back.
 */
const PARTS_ACTIONS_ENABLED = false;

/**
 * Steps by (state, substate) — gate forward, so a reload re-derives the right step from SN:
 *   16     → Accept
 *   17 / ∅ → Travel Start
 *   17 / 8 → Intervention Start   ← On Route; starting the intervention implies the FE arrived
 *   18     → Intervention End
 * There is no discrete Travel End step: Intervention Start is the "arrived & working" marker
 * (it also stops the live-location broadcast, see the wf-intervention-start handler).
 */
function deriveWorkflowState(
  stateCode: string | null | undefined,
  substateCode: string | null | undefined,
): WorkflowState {
  const onRoute = (substateCode ?? '') === SUBSTATE_ON_ROUTE;
  switch (stateCode ?? '') {
    case '16':
      return {
        enabled: {
          accept: true,
          travel_start: false,
          intervention_start: false,
          intervention_end: false,
        },
        next: 'accept',
      };
    case '17':
      // Accepted: offer Travel Start until On Route (substate 8), then Intervention Start.
      return onRoute
        ? {
            enabled: {
              accept: false,
              travel_start: false,
              intervention_start: true,
              intervention_end: false,
            },
            next: 'intervention_start',
          }
        : {
            enabled: {
              accept: false,
              travel_start: true,
              intervention_start: false,
              intervention_end: false,
            },
            next: 'travel_start',
          };
    case '18':
      return {
        enabled: {
          accept: false,
          travel_start: false,
          intervention_start: false,
          intervention_end: true,
        },
        next: 'intervention_end',
      };
    default:
      return {
        enabled: {
          accept: false,
          travel_start: false,
          intervention_start: false,
          intervention_end: false,
        },
        next: null,
      };
  }
}

type ActiveModal =
  | 'cad'
  | 'cus'
  | 'cna'
  | 'cra'
  | 'tpm'
  | 'cancel'
  | 'comment'
  | 'wf-accept'
  | 'wf-travel-start'
  | 'wf-intervention-start'
  | 'wf-intervention-end'
  | null;

/**
 * Persistent FE preference for sharing live geolocation. Some engineers don't want to be
 * tracked; this lives in localStorage (per-browser) so the choice survives reloads without
 * adding a server-side preference column. The flag gates *only* live broadcasting — every
 * SN status mutation (Travel Start etc.) still fires regardless, so the customer-facing
 * workflow on ServiceNow's side is unaffected.
 */
const TRACKING_PREF_KEY = 'smartteams-app:tracking-allowed';

function readTrackingAllowed(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const stored = window.localStorage.getItem(TRACKING_PREF_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

function writeTrackingAllowed(allowed: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TRACKING_PREF_KEY, String(allowed));
  } catch {
    // localStorage can throw in private-mode Safari; ignore — the in-memory state still works.
  }
}

export const TicketDetailPage = stateful(({ number }: { number: string }) => {
  const [isTracking, setIsTracking] = useState(false);
  const [trackingAllowed, setTrackingAllowed] = useState<boolean>(readTrackingAllowed());
  const [geoLat, setGeoLat] = useState<number | null>(null);
  const [geoLng, setGeoLng] = useState<number | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewRating, setReviewRating] = useState<number>(0);
  const [reviewSuccess, setReviewSuccess] = useState<boolean | null>(null);
  const [reviewComment, setReviewComment] = useState<string>('');
  const [reviewSubmittedAt, setReviewSubmittedAt] = useState<number>(0);
  const [ticketRefreshKey, setTicketRefreshKey] = useState<number>(0);
  const [feGeoRefreshKey, setFeGeoRefreshKey] = useState<number>(0);

  // ETA window — categorical bucket sent via `eta_window` (spec §4). Null until the
  // FE picks one. Independent of `en_route`; chip taps fire immediately.
  const [etaWindow, setEtaWindow] = useState<SNEtaWindow | null>(null);

  const { can: canReview } = useABAC(accessController, createPermissionPayload(SmartTeamsAppPermission.TicketReview));

  const myUserQuery = useQuery(
    () =>
      getMyAppUser()({
        _id: 1,
        userGroup: 1,
        serviceNowFEId: 1,
      }),
    [],
  );

  useEffect(() => {
    if (!isTracking) return;

    if (!navigator.geolocation) {
      setGeoError(m.ticket_map_not_supported());
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      pos => {
        setGeoLat(pos.coords.latitude);
        setGeoLng(pos.coords.longitude);
        setGeoError(null);

        const now = Date.now();
        if (now - lastGeoUpdate > GEO_UPDATE_INTERVAL) {
          lastGeoUpdate = now;
          updateMyGeolocation(
            pos.coords.latitude,
            pos.coords.longitude,
            pos.coords.accuracy,
          )({ _id: 1 }).catch(() => {});
        }
      },
      err => setGeoError(err.message),
      { enableHighAccuracy: true, maximumAge: 10000 },
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [isTracking]);

  // Stop the current broadcast (local watcher + server isTracking flag) without touching the
  // persistent `trackingAllowed` preference, so a later Travel Start can resume. Used on arrival
  // (Travel End / Intervention Start) and when the toggle is switched off.
  const stopBroadcast = () => {
    setIsTracking(false);
    setGeoLat(null);
    setGeoLng(null);
    setGeoError(null);
    void stopMyGeolocationTracking()().catch(() => {});
  };

  // Flip the tracking preference. Off → stop broadcasting. On → only resume when already en route
  // (we don't auto-resume otherwise — the FE re-presses Travel Start to opt back in).
  const handleTrackingToggle = (next: boolean, opts?: { resumeIfEnRoute?: boolean }) => {
    setTrackingAllowed(next);
    writeTrackingAllowed(next);
    if (!next) {
      stopBroadcast();
    } else if (opts?.resumeIfEnRoute) {
      setIsTracking(true);
    }
  };

  const ticketQuery = useQuery(
    () =>
      getTicketByNumber(number)({
        number: 1,
        title: 1,
        description: 1,
        assignedToSysId: 1,
        assignedToName: 1,
        state: 1,
        stateLabel: 1,
        substate: 1,
        substateLabel: 1,
        etaWindow: 1,
        locationLabel: 1,
        locationLat: 1,
        locationLng: 1,
        expectedStart: 1,
        expectedStartDisplay: 1,
        windowStart: 1,
        windowEnd: 1,
        fulfilmentStatusLabel: 1,
        workNotes: { date: 1, html: 1 },
        clientName: 1,
        clientCode: 1,
        contactName: 1,
        contactPhone: 1,
        contactEmail: 1,
        pendingAction: 1,
        enabledProblemReports: 1,
        pudoLocationLabel: 1,
        pudoLocationLat: 1,
        pudoLocationLng: 1,
        parts: {
          description: 1,
          modelNumber: 1,
          manufacturer: 1,
          quantity: 1,
          stage: 1,
          sourced: 1,
          delivered: 1,
          returnDefective: 1,
          assetLocation: 1,
          poNumber: 1,
        },
      }),
    [number, ticketRefreshKey],
  );

  const myReviewQuery = useQuery(
    () =>
      getMyTicketReview(number)({
        _id: 1,
        rating: 1,
        successfullyCompleted: 1,
        comment: 1,
        createdAt: 1,
      }).catch(() => null),
    [number, reviewSubmittedAt],
  );

  // Customer/admin view of the assigned FE's live position. FE side draws their own marker
  // from local browser geolocation, so no need to read it back for them.
  const feGeoQuery = useQuery(
    () =>
      getFEGeolocationForTicket(number)({
        latitude: 1,
        longitude: 1,
        accuracy: 1,
        updatedAt: 1,
        isTracking: 1,
      }).catch(() => null),
    [number, feGeoRefreshKey],
  );

  // Keep the last successfully fetched ticket in component state so refetches
  // (driven by `ticketRefreshKey`) don't blank the whole page while loading.
  const [cachedTicket, setCachedTicket] = useState<typeof ticketQuery.value>(null);
  useEffect(() => {
    if (ticketQuery.value) setCachedTicket(ticketQuery.value);
  }, [ticketQuery.value]);

  const ticket = ticketQuery.value ?? cachedTicket;

  // Keep the last resolved FE position so the 20s customer/admin poll doesn't tear down + rebuild
  // the MapBox each cycle (losing zoom/pan). A poll that resolves to null still clears the map.
  const [cachedFeGeo, setCachedFeGeo] = useState<typeof feGeoQuery.value>(null);
  useEffect(() => {
    if (!feGeoQuery.loading) setCachedFeGeo(feGeoQuery.value ?? null);
  }, [feGeoQuery.loading, feGeoQuery.value]);
  const feGeo = feGeoQuery.loading ? cachedFeGeo : feGeoQuery.value ?? null;
  const isPendingAction = !!ticket?.pendingAction;

  // Live self-update: while this page is open we subscribe to the ticket's change signal.
  // The inbound ServiceNow webhook (and any FE action) bumps the tick, and we re-fetch — so
  // the ticket updates itself when ServiceNow changes, without constant polling. The set of
  // open detail pages is exactly the server-side subscriber set (see api/_ticket-events.ts).
  const ticketChange = useQuery(() => subscribeTicketChanges(number)(), [number]);
  useEffect(() => {
    if (!ticketChange.value) return;
    setTicketRefreshKey(Date.now());
    // Also refresh the customer/admin position view on a nudge — but not for the assignee FE (their
    // map uses the local watcher; bumping would just cost a wasted getFEGeolocationForTicket → SN call).
    const me = myUserQuery.value;
    const viewerIsAssigneeFE =
      me?.userGroup === SmartTeamsAppUserGroup.FieldEngineer &&
      !!me.serviceNowFEId &&
      me.serviceNowFEId === ticket?.assignedToSysId;
    if (!viewerIsAssigneeFE) setFeGeoRefreshKey(Date.now());
  }, [ticketChange.value]);

  useEffect(() => {
    if (!isPendingAction) return;
    const interval = setInterval(() => setTicketRefreshKey(Date.now()), 2000);
    return () => clearInterval(interval);
  }, [isPendingAction]);

  // Seed the ETA-window chip highlight from the value SN holds (ticket_details.eta_window), so it
  // survives reloads and reflects what the FE previously set rather than resetting to none.
  useEffect(() => {
    const w = ticket?.etaWindow;
    if (w && ETA_WINDOW_KEYS.has(w)) setEtaWindow(w as SNEtaWindow);
  }, [ticket?.etaWindow]);

  if (!ticket) {
    if (ticketQuery.loading) {
      return html`
        <div class="flex items-center justify-center h-full">
          <span class="loading loading-spinner loading-lg text-primary"></span>
        </div>
      `;
    }
    return html`
      <div class="flex flex-col items-center justify-center h-full gap-4 p-4">
        <span class="icon-[material-symbols--error-outline] text-4xl text-error"></span>
        <p class="text-error">${m.error_generic()}</p>
        <a href="${router.link('tickets')}" class="btn btn-ghost btn-sm gap-1">
          <span class="icon-[material-symbols--arrow-back] text-lg"></span>
          ${m.ticket_back_to_list()}
        </a>
      </div>
    `;
  }
  const me = myUserQuery.value;
  const isAssigneeFE =
    me?.userGroup === SmartTeamsAppUserGroup.FieldEngineer &&
    !!me.serviceNowFEId &&
    me.serviceNowFEId === ticket.assignedToSysId;
  const isCustomerOfTicket = me?.userGroup === SmartTeamsAppUserGroup.Customer;
  const isAdmin = me?.userGroup === SmartTeamsAppUserGroup.Admin;
  // WOT terminal states: 3 = Closed Complete, 4 = Closed Incomplete, 7 = Cancelled.
  // 19 = Resolved: intervention_finished lands the task here (spec §4.3) — the FE's work is done
  // and no further field actions apply, so for the workflow UI, tracking and live map we treat it
  // like a closed ticket. The customer review still waits for a true close (3/4/7) — see below.
  const isTicketClosed =
    ticket.state === '3' || ticket.state === '4' || ticket.state === '7' || ticket.state === '19';
  // Per-customer opt-in "Report a problem" buttons (SmartTeamsCustomer.enabledProblemReports,
  // attached by getTicketByNumber). A button shows only if its key is enabled; the action
  // mutations enforce the same set server-side. (Cancellation is always available — not in here.)
  const enabledReports = new Set(ticket.enabledProblemReports ?? []);

  // Customer/Admin watch a passive, server-cached FE position; poll it while the page is open
  // so the marker keeps tracking the FE. The assignee FE draws their own live position from the
  // browser geolocation watcher, so they don't need this refresh.
  useEffect(() => {
    if (isAssigneeFE || isTicketClosed) return;
    const interval = setInterval(() => setFeGeoRefreshKey(Date.now()), 20_000);
    return () => clearInterval(interval);
  }, [isAssigneeFE, isTicketClosed]);

  // Resume broadcasting after a reload while still On Route. `isTracking` is in-memory and resets
  // on every mount, but a ticket sitting at Accepted/On Route (17/8) means the FE is mid-travel and
  // should still be sharing — Travel Start is already disabled at this point, so without this they
  // had no way to get back live. Respects the persistent opt-out; stops naturally once the ticket
  // leaves On Route (Intervention Start → 18, which also calls stopBroadcast).
  useEffect(() => {
    if (isAssigneeFE && trackingAllowed && !isTicketClosed && ticket.state === '17' && ticket.substate === SUBSTATE_ON_ROUTE) {
      setIsTracking(true);
    }
  }, [isAssigneeFE, trackingAllowed, isTicketClosed, ticket.state, ticket.substate]);

  const runAction = async (key: string, fn: () => Promise<unknown>, successMsg: string) => {
    if (actionBusy) return;
    setActionBusy(key);
    setActionError(null);
    setActionStatus(null);
    try {
      await fn();
      setActionStatus(successMsg);
      setTicketRefreshKey(Date.now());
    } catch (err: unknown) {
      setActionError(errorOrFallback(err, m.ticket_action_failed()));
    } finally {
      setActionBusy(null);
    }
  };

  // Status banner auto-dismisses after a few seconds — feels like a toast without
  // pulling in another component, and keeps the next action's feedback clean.
  useEffect(() => {
    if (!actionStatus) return;
    const t = setTimeout(() => setActionStatus(null), 4000);
    return () => clearTimeout(t);
  }, [actionStatus]);

  /**
   * Modal-bound action runner. Differs from `runAction` in that errors stay inside the
   * modal (so the FE can fix the input and retry) instead of bubbling up to the page-level
   * banner; success closes the modal and refreshes the ticket.
   *
   * `successMsg` is optional: state-changing actions (accept, travel start, intervention
   * start/end, cancellation) raise the full-screen pending overlay and then visibly advance the
   * ticket, so a success banner on top is redundant — they omit it. Only no-blocker actions whose
   * effect isn't otherwise visible (the CNA/CAD/CRA/CUS exceptions) pass a message to confirm.
   */
  const runModalAction = async (
    key: string,
    fn: () => Promise<unknown>,
    successMsg?: string,
  ): Promise<boolean> => {
    if (actionBusy) return false;
    setActionBusy(key);
    setModalError(null);
    setActionError(null);
    try {
      await fn();
      if (successMsg) setActionStatus(successMsg);
      setActiveModal(null);
      setTicketRefreshKey(Date.now());
      return true;
    } catch (err: unknown) {
      setModalError(errorOrFallback(err, m.ticket_action_failed()));
      return false;
    } finally {
      setActionBusy(null);
    }
  };

  /**
   * CAD: arrange a new date with the customer. If `securedSite` is on, also fire
   * access_not_granted so SN's CRA path is recorded — order is access-first, then
   * the reschedule, so a refetch reflects the final reschedule state.
   */
  const submitCad = async (cad: CadModalValues) => {
    await runModalAction(
      'cad',
      async () => {
        if (cad.securedSite) {
          await ticketActionAccessNotGranted(ticket.number!, 'SECURED_SITE')({ number: 1 });
        }
        await ticketActionCustomerRequestedReschedule(
          ticket.number!,
          cad.agreedDate,
          cad.newContactName,
          cad.newContactPhone,
          cad.notes,
          cad.confirmedByName,
          cad.confirmedByPhone,
        )({ number: 1 });
      },
      m.ticket_action_cad_success(),
    );
  };

  /**
   * CUS: report Just-Go-unsuccessful, optionally piggy-backing a CAD reschedule.
   * `unsuccessful_visit` always fires; the reschedule is conditional.
   */
  const submitCus = async (
    values:
      | { arrangedNewDate: false; notes: string }
      | { arrangedNewDate: true; cad: CadModalValues; notes: string },
  ) => {
    await runModalAction(
      'cus',
      async () => {
        await ticketActionUnsuccessfulVisit(ticket.number!, 'JUST_GO', values.notes)({ number: 1 });
        if (values.arrangedNewDate) {
          const cad = values.cad;
          if (cad.securedSite) {
            await ticketActionAccessNotGranted(ticket.number!, 'SECURED_SITE')({ number: 1 });
          }
          await ticketActionCustomerRequestedReschedule(
            ticket.number!,
            cad.agreedDate,
            cad.newContactName,
            cad.newContactPhone,
            undefined,
            cad.confirmedByName,
            cad.confirmedByPhone,
          )({ number: 1 });
        }
      },
      values.arrangedNewDate
        ? m.ticket_action_cus_success_with_reschedule()
        : m.ticket_action_cus_success(),
    );
  };

  return html`
    ${isPendingAction
      ? html`
          <div class="fixed inset-0 z-50 bg-base-300/60 flex items-center justify-center backdrop-blur-sm">
            <div class="bg-base-100 rounded-2xl shadow-xl p-6 flex flex-col items-center gap-3 max-w-xs mx-4">
              <span class="loading loading-spinner loading-lg text-primary"></span>
              <p class="text-sm font-semibold text-center">${m.ticket_pending_action_title()}</p>
              <p class="text-xs text-base-content/60 text-center">${m.ticket_pending_action_subtitle()}</p>
            </div>
          </div>
        `
      : nothing}
    <div class="px-4 py-5 max-w-3xl mx-auto flex flex-col gap-5">
      <!-- Back + Header -->
      <div>
        <a href="${router.link('tickets')}" class="btn btn-ghost btn-sm gap-1 -ml-2 mb-2">
          <span class="icon-[material-symbols--arrow-back] text-lg"></span>
          ${m.ticket_back_to_list()}
        </a>

        <div class="flex items-center gap-2 mb-1 flex-wrap">
          <span class="font-mono text-xs bg-base-200 px-2 py-0.5 rounded">${ticket.number}</span>
          ${ticket.stateLabel
            ? html`<span class="badge badge-sm ${STATE_BADGE_CLASS[ticket.state ?? ''] ?? 'badge-neutral'}">${ticket.stateLabel}</span>`
            : nothing}
          ${ticket.substate === SUBSTATE_ON_ROUTE && ticket.substateLabel
            ? html`<span class="badge badge-sm badge-outline badge-info">${ticket.substateLabel}</span>`
            : nothing}
          ${isAssigneeFE && SVR_ENABLED
            ? html`
                <a
                  href="${router.link('svrPage', { params: { number: ticket.number! } })}"
                  class="btn btn-outline btn-xs gap-1 ml-auto"
                >
                  <span class="icon-[material-symbols--description] text-sm"></span>
                  ${m.ticket_generate_svr()}
                </a>
              `
            : nothing}
        </div>
        <h1 class="text-2xl font-bold leading-tight">${ticket.title || m.ticket_no_title()}</h1>

        ${ticket.assignedToName
          ? html`
              <div class="flex items-center gap-2 mt-3 text-sm text-base-content/60">
                <span class="icon-[material-symbols--engineering] text-lg"></span>
                ${m.ticket_assigned_to()}: ${ticket.assignedToName}
              </div>
            `
          : nothing}
        ${ticket.locationLabel
          ? (() => {
              const href = mapsHref(ticket.locationLabel, ticket.locationLat, ticket.locationLng);
              return html`
                <div class="flex items-start gap-2 mt-1 text-sm text-base-content/60">
                  <span class="icon-[material-symbols--location-on-outline] text-lg shrink-0"></span>
                  ${href
                    ? html`<a class="link" href="${href}" target="_blank" rel="noopener">${ticket.locationLabel}</a>`
                    : html`<span>${ticket.locationLabel}</span>`}
                </div>
              `;
            })()
          : nothing}
      </div>

      <!-- Date & Time -->
      <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm">
        <div class="px-4 pt-4 pb-2 flex items-center gap-2">
          <span class="icon-[material-symbols--schedule-outline] text-lg text-primary"></span>
          <h3 class="font-semibold">${m.ticket_date_time()}</h3>
        </div>
        <div class="px-4 pb-4">
          ${ticket.expectedStartDisplay
            ? html`<p class="text-sm text-base-content/80">${ticket.expectedStartDisplay}</p>`
            : ticket.windowStart
              ? html`<p class="text-sm text-base-content/80">
                  ${windowDate(ticket.windowStart)} · ${windowLabel(ticket.windowStart, ticket.windowEnd)}
                </p>`
              : html`<p class="text-sm text-base-content/50 italic">${m.ticket_no_date()}</p>`}
          ${ticket.etaWindow
            ? html`<p class="text-sm mt-1">
                <span class="text-base-content/60">${m.eta_window_card_title()}:</span>
                ${etaWindowLabel(ticket.etaWindow)}
              </p>`
            : nothing}
        </div>
      </div>

      <!-- Geolocation: FE writes their own browser position; Customer/Admin reads server-cached position.
           Hidden once the ticket is closed/cancelled — the live route stops being meaningful. -->
      ${(() => {
        if (isTicketClosed) return nothing;
        const mapToken = config.get('MAPBOX_ACCESS_TOKEN');
        if (!mapToken) return nothing;

        // Customer / Admin: passive read from the server. Server only returns when the FE is tracking.
        if (!isAssigneeFE) {
          if (!isCustomerOfTicket && !isAdmin) return nothing;
          const geo = feGeo;
          if (!geo || geo.latitude == null || geo.longitude == null) {
            return html`
              <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm p-4">
                <div class="flex items-center gap-2">
                  <span class="icon-[material-symbols--location-off] text-lg text-base-content/40"></span>
                  <span class="text-sm text-base-content/60">${m.ticket_fe_not_sharing()}</span>
                </div>
              </div>
            `;
          }
          const feLat = geo.latitude;
          const feLng = geo.longitude;
          const hasDest = ticket.locationLat != null && ticket.locationLng != null;
          const dLat = ticket.locationLat ?? feLat;
          const dLng = ticket.locationLng ?? feLng;
          return html`
            <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm overflow-hidden">
              <div class="px-4 pt-4 pb-2 flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="icon-[material-symbols--my-location] text-lg text-primary"></span>
                  <h3 class="font-semibold">${m.ticket_fe_location_title()}</h3>
                </div>
                <span class="text-xs text-base-content/50">${m.ticket_fe_updated_at({ at: toLocalTime(geo.updatedAt) })}</span>
              </div>
              ${MapBox({
                accessToken: mapToken,
                height: '350px',
                mapOptions: { center: [feLng, feLat], zoom: 10 },
                markerGroups: [
                  {
                    markers: [{ lat: feLat, lng: feLng }],
                    clusterOptions: { enabled: false },
                    template: html`
                      <div style="position:relative;width:22px;height:22px;">
                        <div
                          style="position:absolute;inset:0;background:rgba(59,130,246,0.2);border-radius:50%;animation:fe-pulse 2s ease-out infinite;"
                        ></div>
                        <div
                          style="position:absolute;inset:3px;background:#3b82f6;border:2.5px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3);"
                        ></div>
                      </div>
                      <style>
                        @keyframes fe-pulse {
                          0% {
                            transform: scale(1);
                            opacity: 1;
                          }
                          100% {
                            transform: scale(2.5);
                            opacity: 0;
                          }
                        }
                      </style>
                    `,
                  },
                  ...(hasDest
                    ? [
                        {
                          markers: [{ lat: dLat, lng: dLng }],
                          clusterOptions: { enabled: false },
                          template: html`
                            <div style="position:relative;width:28px;height:36px;">
                              <div
                                style="width:28px;height:28px;background:#ef4444;border:2.5px solid white;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.35);"
                              ></div>
                              <div
                                style="position:absolute;top:6px;left:6px;width:16px;height:16px;display:flex;align-items:center;justify-content:center;"
                              >
                                <div style="width:8px;height:8px;background:white;border-radius:50%;"></div>
                              </div>
                            </div>
                          `,
                        },
                      ]
                    : []),
                ],
                customizeMap: map => {
                  if (hasDest) {
                    addDrivingRouteWhenReady(map, mapToken, { lat: feLat, lng: feLng }, { lat: dLat, lng: dLng });
                  }
                  map.once('idle', () => {
                    try {
                      if (hasDest) {
                        map.fitBounds(
                          [
                            [Math.min(feLng, dLng), Math.min(feLat, dLat)],
                            [Math.max(feLng, dLng), Math.max(feLat, dLat)],
                          ],
                          { padding: 60, duration: 1000 },
                        );
                      } else {
                        map.flyTo({ center: [feLng, feLat], zoom: 12, duration: 800 });
                      }
                    } catch {}
                  });
                },
              })}
            </div>
          `;
        }

        // FE-side toggle: the persistent preference that controls whether broadcasting
        // happens at all. Rendered in every FE-side card header so it's always reachable.
        const trackingToggle = html`
          <label class="flex items-center gap-2 cursor-pointer">
            <span class="text-xs text-base-content/60 hidden sm:inline">${m.ticket_location_share_label()}</span>
            <input
              type="checkbox"
              class="toggle toggle-sm toggle-primary"
              .checked=${trackingAllowed}
              @change=${(e: Event) =>
                handleTrackingToggle((e.target as HTMLInputElement).checked, {
                  // Resume only while genuinely en route (substate 8), so the toggle can't
                  // re-broadcast before Travel Start or after the intervention has started.
                  resumeIfEnRoute: ticket.state === '17' && ticket.substate === SUBSTATE_ON_ROUTE,
                })}
              aria-label="${m.ticket_location_share_label()}"
            />
          </label>
        `;

        // Opted-out: explicit FE preference. Hide the map entirely; ServiceNow workflow
        // (Travel Start, etc.) keeps working but no live position is broadcast.
        if (!trackingAllowed) {
          return html`
            <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm">
              <div class="px-4 pt-4 pb-2 flex items-center justify-between gap-2">
                <div class="flex items-center gap-2">
                  <span class="icon-[material-symbols--location-off] text-lg text-base-content/50"></span>
                  <h3 class="font-semibold">${m.ticket_location_opted_out_title()}</h3>
                </div>
                ${trackingToggle}
              </div>
              <div class="px-4 pb-4">
                <p class="text-sm text-base-content/70 text-pretty">
                  ${m.ticket_location_opted_out_message()}
                </p>
                <p class="text-xs text-base-content/50 mt-2">${m.ticket_location_share_help()}</p>
              </div>
            </div>
          `;
        }

        // FE-assignee: before tracking starts, render a calm placeholder card with
        // a non-interactive destination preview so the slot reserved for the live map
        // doesn't disappear. Tapping Travel Start swaps this card for the active map.
        if (!isTracking) {
          const hasDest = ticket.locationLat != null && ticket.locationLng != null;
          return html`
            <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm overflow-hidden">
              <div class="px-4 pt-4 pb-2 flex items-center justify-between gap-2">
                <div class="flex items-center gap-2">
                  <span class="icon-[material-symbols--my-location] text-lg text-primary"></span>
                  <h3 class="font-semibold">${m.ticket_map_title()}</h3>
                </div>
                ${trackingToggle}
              </div>
              <div class="px-4 pb-4">
                <p class="text-sm text-base-content/70 text-pretty">${m.ticket_fe_pre_tracking_message()}</p>
                ${ticket.locationLabel
                  ? (() => {
                      const href = mapsHref(ticket.locationLabel, ticket.locationLat, ticket.locationLng);
                      return html`
                        <div class="mt-3 flex items-start gap-2 text-sm">
                          <span
                            class="icon-[material-symbols--location-on-outline] text-base text-base-content/50 shrink-0 h-lh"
                          ></span>
                          ${href
                            ? html`<a class="link text-base-content/70 text-pretty" href="${href}" target="_blank" rel="noopener"
                                >${ticket.locationLabel}</a
                              >`
                            : html`<span class="text-base-content/70 text-pretty">${ticket.locationLabel}</span>`}
                        </div>
                      `;
                    })()
                  : nothing}
              </div>
              ${hasDest
                ? html`
                    <div class="border-t border-base-300/60 [filter:saturate(0.85)] opacity-90">
                      ${MapBox({
                        accessToken: mapToken,
                        height: '180px',
                        mapOptions: {
                          center: [ticket.locationLng!, ticket.locationLat!],
                          zoom: 12,
                        },
                        markerGroups: [
                          {
                            markers: [{ lat: ticket.locationLat!, lng: ticket.locationLng! }],
                            clusterOptions: { enabled: false },
                            template: html`
                              <div style="position:relative;width:28px;height:36px;">
                                <div
                                  style="width:28px;height:28px;background:#ef4444;border:2.5px solid white;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.35);"
                                ></div>
                                <div
                                  style="position:absolute;top:6px;left:6px;width:16px;height:16px;display:flex;align-items:center;justify-content:center;"
                                >
                                  <div style="width:8px;height:8px;background:white;border-radius:50%;"></div>
                                </div>
                              </div>
                            `,
                          },
                        ],
                        customizeMap: map => {
                          map.scrollZoom.disable();
                          map.dragPan.disable();
                          map.doubleClickZoom.disable();
                          map.touchZoomRotate.disable();
                          map.boxZoom.disable();
                          map.keyboard.disable();
                        },
                      })}
                    </div>
                  `
                : nothing}
            </div>
          `;
        }

        if (geoError) {
          return html`
            <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm p-4 flex items-center justify-between gap-2">
              <div class="flex items-center gap-2 min-w-0">
                <span class="icon-[material-symbols--location-off] text-lg text-base-content/40 shrink-0"></span>
                <span class="text-sm text-base-content/60 truncate">${geoError}</span>
              </div>
              ${trackingToggle}
            </div>
          `;
        }

        if (geoLat === null || geoLng === null) {
          return html`
            <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm p-4 flex items-center justify-between gap-2">
              <div class="flex items-center gap-2 min-w-0">
                <span class="loading loading-spinner loading-sm text-primary shrink-0"></span>
                <span class="text-sm text-base-content/60 truncate">${m.ticket_map_loading()}</span>
              </div>
              ${trackingToggle}
            </div>
          `;
        }

        const hasFeDest = ticket.locationLat != null && ticket.locationLng != null;
        const destLat = ticket.locationLat ?? 48.1374;
        const destLng = ticket.locationLng ?? 11.5755;

        return html`
          <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm overflow-hidden">
            <div class="px-4 pt-4 pb-2 flex items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <span class="icon-[material-symbols--my-location] text-lg text-primary"></span>
                <h3 class="font-semibold">${m.ticket_map_title()}</h3>
              </div>
              ${trackingToggle}
            </div>
            ${MapBox({
              accessToken: mapToken,
              height: '350px',
              mapOptions: {
                center: [geoLng, geoLat],
                zoom: 10,
              },
              markerGroups: [
                {
                  markers: [{ lat: geoLat, lng: geoLng }],
                  clusterOptions: { enabled: false },
                  template: html`
                    <div style="position:relative;width:22px;height:22px;">
                      <div
                        style="position:absolute;inset:0;background:rgba(59,130,246,0.2);border-radius:50%;animation:fe-pulse 2s ease-out infinite;"
                      ></div>
                      <div
                        style="position:absolute;inset:3px;background:#3b82f6;border:2.5px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3);"
                      ></div>
                    </div>
                    <style>
                      @keyframes fe-pulse {
                        0% {
                          transform: scale(1);
                          opacity: 1;
                        }
                        100% {
                          transform: scale(2.5);
                          opacity: 0;
                        }
                      }
                    </style>
                  `,
                },
                ...(hasFeDest
                  ? [
                      {
                        markers: [{ lat: destLat, lng: destLng }],
                        clusterOptions: { enabled: false },
                        template: html`
                          <div style="position:relative;width:28px;height:36px;">
                            <div
                              style="width:28px;height:28px;background:#ef4444;border:2.5px solid white;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.35);"
                            ></div>
                            <div
                              style="position:absolute;top:6px;left:6px;width:16px;height:16px;display:flex;align-items:center;justify-content:center;"
                            >
                              <div style="width:8px;height:8px;background:white;border-radius:50%;"></div>
                            </div>
                          </div>
                        `,
                      },
                    ]
                  : []),
              ],
              customizeMap: map => {
                if (hasFeDest) {
                  addDrivingRouteWhenReady(map, mapToken, { lat: geoLat, lng: geoLng }, { lat: destLat, lng: destLng });
                }
                map.once('idle', () => {
                  try {
                    if (hasFeDest) {
                      map.fitBounds(
                        [
                          [Math.min(geoLng, destLng), Math.min(geoLat, destLat)],
                          [Math.max(geoLng, destLng), Math.max(geoLat, destLat)],
                        ],
                        { padding: 60, duration: 1000 },
                      );
                    } else {
                      map.flyTo({ center: [geoLng, geoLat], zoom: 13, duration: 800 });
                    }
                  } catch {}
                });
              },
            })}
          </div>
        `;
      })()}

      <!-- Description -->
      <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm">
        <div class="px-4 pt-4 pb-2 flex items-center gap-2">
          <span class="icon-[material-symbols--description-outline] text-lg text-primary"></span>
          <h3 class="font-semibold">${m.ticket_description()}</h3>
        </div>
        <div class="px-4 pb-4">
          ${ticket.description
            ? html`<p class="text-sm whitespace-pre-wrap leading-relaxed text-base-content/70">
                ${ticket.description}
              </p>`
            : html`<p class="text-sm text-base-content/50 italic">${m.ticket_detail_no_description()}</p>`}
        </div>
      </div>

      <!-- Fulfilment status + work notes — the same info the colleague view shows. Notes are only
           returned to the assigned FE and admins (see getTicketByNumber), so this is empty for customers. -->
      ${ticket.fulfilmentStatusLabel || (ticket.workNotes ?? []).length > 0
        ? html`
            <div class="flex flex-col gap-2">
              ${ticket.fulfilmentStatusLabel
                ? html`<div class="flex items-center gap-1 text-sm text-base-content/70 px-1">
                    <span class="icon-[material-symbols--local-shipping-outline] text-base"></span>
                    ${ticket.fulfilmentStatusLabel}
                  </div>`
                : nothing}
              ${notesBlock(ticket.workNotes)}
            </div>
          `
        : nothing}

      <!-- Client / Contact (rendered when SN ticket_details/list expose these fields) -->
      ${ticket.clientName || ticket.contactName || ticket.contactPhone || ticket.contactEmail
        ? html`
            <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm">
              <div class="px-4 pt-4 pb-2 flex items-center gap-2">
                <span class="icon-[material-symbols--apartment-outline] text-lg text-primary"></span>
                <h3 class="font-semibold">${m.ticket_customer_contact_title()}</h3>
              </div>
              <div class="px-4 pb-4 flex flex-col gap-1 text-sm">
                ${ticket.clientName
                  ? html`<p>
                      <span class="text-base-content/60">${m.ticket_customer_label()}</span> ${ticket.clientName}${ticket.clientCode
                        ? html` <span class="text-base-content/40">(${ticket.clientCode})</span>`
                        : nothing}
                    </p>`
                  : nothing}
                ${ticket.contactName
                  ? html`<p><span class="text-base-content/60">${m.ticket_contact_label()}</span> ${ticket.contactName}</p>`
                  : nothing}
                ${ticket.contactPhone
                  ? html`<p>
                      <span class="text-base-content/60">${m.ticket_phone_label()}</span>
                      <a class="link" href="tel:${ticket.contactPhone}">${ticket.contactPhone}</a>
                    </p>`
                  : nothing}
                ${ticket.contactEmail
                  ? html`<p>
                      <span class="text-base-content/60">${m.ticket_email_label()}</span>
                      <a class="link" href="mailto:${ticket.contactEmail}">${ticket.contactEmail}</a>
                    </p>`
                  : nothing}
              </div>
            </div>
          `
        : nothing}

      <!-- Parts — informational, visible to every viewer (read-only manifest + PUDO depot).
           FE actions on parts live in the "Parts handling" section below; the FE-only extras
           here (return-defective flag, install training) are gated to the assignee. -->
      ${(() => {
        const parts = ticket.parts ?? [];
        if (parts.length === 0 && !ticket.pudoLocationLabel) return nothing;
        const pudoHref = mapsHref(ticket.pudoLocationLabel, ticket.pudoLocationLat, ticket.pudoLocationLng);
        return html`
          <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm">
            <div class="px-4 pt-4 pb-2 flex items-center gap-2">
              <span class="icon-[material-symbols--inventory-2-outline] text-lg text-primary shrink-0 h-lh"></span>
              <div class="flex-1">
                <h3 class="font-semibold leading-tight">${m.ticket_parts_title()}</h3>
                <p class="text-xs text-base-content/60 mt-0.5">${m.ticket_parts_help()}</p>
              </div>
              ${parts.length > 0
                ? html`<span class="badge badge-sm badge-neutral tabular-nums shrink-0">${parts.length}</span>`
                : nothing}
            </div>

            ${ticket.pudoLocationLabel
              ? html`
                  <div class="px-4 pb-3">
                    <div class="flex items-start gap-2 rounded-xl bg-base-200/60 p-3">
                      <span class="icon-[material-symbols--storefront-outline] text-base text-base-content/60 shrink-0 h-lh"></span>
                      <div class="flex-1 min-w-0">
                        <p class="text-xs font-medium text-base-content/60">${m.ticket_pudo_title()}</p>
                        <p class="text-sm text-pretty">${ticket.pudoLocationLabel}</p>
                      </div>
                      ${pudoHref
                        ? html`<a
                            class="btn btn-ghost btn-xs gap-1 text-primary shrink-0"
                            href="${pudoHref}"
                            target="_blank"
                            rel="noopener"
                          >
                            <span class="icon-[material-symbols--directions-outline] text-sm h-lh"></span>
                            ${m.ticket_pudo_directions()}
                          </a>`
                        : nothing}
                    </div>
                  </div>
                `
              : nothing}

            ${parts.length > 0
              ? html`
                  <ul role="list" class="flex flex-col">
                    ${parts.map(part => {
                      const fulfil = partFulfilmentInfo(part);
                      const subline = [part.manufacturer, part.modelNumber].filter(Boolean).join(' · ');
                      return html`
                        <li class="px-4 py-3 border-t border-base-300/60 flex flex-col gap-1.5">
                          <div class="flex items-start justify-between gap-3">
                            <div class="flex-1 min-w-0">
                              <p class="text-sm font-medium text-pretty">${part.description || m.ticket_part_unnamed()}</p>
                              ${subline
                                ? html`<p class="text-xs text-base-content/50">${subline}</p>`
                                : nothing}
                            </div>
                            ${fulfil.label
                              ? html`<span class="badge badge-sm ${fulfil.badgeClass} shrink-0">${fulfil.label}</span>`
                              : nothing}
                          </div>

                          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-base-content/60">
                            ${part.quantity
                              ? html`<span class="inline-flex items-center gap-1 tabular-nums">
                                  <span class="icon-[material-symbols--tag] text-sm h-lh shrink-0"></span>
                                  ${m.ticket_parts_qty({ qty: part.quantity })}
                                </span>`
                              : nothing}
                            ${part.assetLocation
                              ? html`<span class="inline-flex items-center gap-1">
                                  <span class="icon-[material-symbols--inventory-2-outline] text-sm h-lh shrink-0"></span>
                                  ${part.assetLocation}
                                </span>`
                              : nothing}
                            ${part.poNumber
                              ? html`<span class="inline-flex items-center gap-1 tabular-nums">
                                  <span class="icon-[material-symbols--receipt-long-outline] text-sm h-lh shrink-0"></span>
                                  ${part.poNumber}
                                </span>`
                              : nothing}
                          </div>

                          ${isAssigneeFE && part.returnDefective
                            ? html`<div class="flex flex-wrap items-center gap-2">
                                <span class="badge badge-sm badge-warning gap-1 py-1 pr-2 pl-1">
                                  <span class="icon-[material-symbols--keyboard-return] text-sm h-lh shrink-0"></span>
                                  ${m.ticket_part_return_defective()}
                                </span>
                              </div>`
                            : nothing}
                        </li>
                      `;
                    })}
                  </ul>
                `
              : nothing}
          </div>
        `;
      })()}

      <!-- Status Actions — assignee FE only. Server re-checks assignee on every action. -->
      ${isAssigneeFE
        ? (() => {
            const wf = deriveWorkflowState(ticket.state, ticket.substate);

            const wfButton = (params: { key: WorkflowKey; label: string; icon: string; onClick: () => void }) => {
              const enabled = wf.enabled[params.key] && !isTicketClosed;
              const isNext = wf.next === params.key && enabled;
              const buttonClass = isNext ? 'btn btn-sm btn-primary gap-1' : 'btn btn-sm btn-outline gap-1';
              return html`
                <button
                  type="button"
                  class="${buttonClass} relative justify-start"
                  ?disabled=${actionBusy != null || !enabled || isPendingAction}
                  @click=${params.onClick}
                >
                  <span class="${params.icon} text-base"></span>
                  <span class="flex-1 text-left">${params.label}</span>
                  ${isNext
                    ? html`<span class="badge badge-xs badge-warning shrink-0">${m.ticket_action_next_step()}</span>`
                    : nothing}
                </button>
              `;
            };

            const exceptionButton = (params: { label: string; icon: string; tone: 'default' | 'error'; modal: ActiveModal }) => html`
              <button
                type="button"
                class="btn btn-sm gap-1 ${params.tone === 'error' ? 'btn-outline btn-error' : 'btn-outline'}"
                ?disabled=${actionBusy != null || isTicketClosed || isPendingAction}
                @click=${() => {
                  setModalError(null);
                  setActiveModal(params.modal);
                }}
              >
                <span class="${params.icon} text-base"></span>
                ${params.label}
              </button>
            `;

            return html`
              <div class="flex flex-col gap-3">
                ${actionStatus
                  ? html`
                      <div class="alert alert-success text-sm py-2 shadow-sm">
                        <span class="icon-[material-symbols--check-circle-outline] text-base"></span>
                        ${actionStatus}
                      </div>
                    `
                  : nothing}
                ${actionError
                  ? html`
                      <div class="alert alert-error text-sm py-2 shadow-sm">
                        <span class="icon-[material-symbols--error-outline] text-base"></span>
                        ${actionError}
                      </div>
                    `
                  : nothing}
                ${isTicketClosed
                  ? html`
                      <div class="alert alert-info text-sm py-2">
                        <span class="icon-[material-symbols--info-outline] text-base"></span>
                        ${m.ticket_state_closed_banner()}
                      </div>
                    `
                  : nothing}

                <!-- ETA window — categorical bucket sent to the customer. Visible while
                     the ticket is in Assigned (16) or Accepted (17). Chip taps fire eta_window. -->
                ${(ticket.state === '16' || ticket.state === '17') && !isTicketClosed
                  ? (() => {
                      const windows: Array<{ value: SNEtaWindow; label: string }> = [
                        { value: 'early_morning', label: m.eta_window_early_morning() },
                        { value: 'morning', label: m.eta_window_morning() },
                        { value: 'midday', label: m.eta_window_midday() },
                        { value: 'afternoon', label: m.eta_window_afternoon() },
                        { value: 'evening', label: m.eta_window_evening() },
                      ];
                      return html`
                        <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm">
                          <div class="px-4 pt-4 pb-2 flex items-center gap-2">
                            <span class="icon-[material-symbols--schedule-outline] text-lg text-primary"></span>
                            <div class="flex-1">
                              <h3 class="font-semibold leading-tight">${m.eta_window_card_title()}</h3>
                              <p class="text-xs text-base-content/60 mt-0.5">${m.eta_window_card_help()}</p>
                            </div>
                          </div>
                          <div class="px-4 pb-4">
                            <div class="flex flex-wrap gap-2">
                              ${windows.map(
                                w => html`
                                  <button
                                    type="button"
                                    class="btn btn-sm ${etaWindow === w.value ? 'btn-primary' : 'btn-outline'}"
                                    ?disabled=${actionBusy != null || isPendingAction}
                                    @click=${() =>
                                      runAction(
                                        `eta_window:${w.value}`,
                                        async () => {
                                          await ticketActionEtaWindow(ticket.number!, w.value)({ number: 1 });
                                          setEtaWindow(w.value);
                                        },
                                        m.eta_window_set_success(),
                                      )}
                                  >
                                    ${w.label}
                                  </button>
                                `,
                              )}
                            </div>
                          </div>
                        </div>
                      `;
                    })()
                  : nothing}

                <!-- Workflow -->
                <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm">
                  <div class="px-4 pt-4 pb-2 flex items-center gap-2">
                    <span class="icon-[material-symbols--rocket-launch-outline] text-lg text-primary"></span>
                    <h3 class="font-semibold flex-1">${m.ticket_section_workflow_title()}</h3>
                  </div>
                  <div class="px-4 pb-4 flex flex-col gap-3">
                    <p class="text-xs text-base-content/60">${m.ticket_section_workflow_help()}</p>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      ${wfButton({
                        key: 'accept',
                        label: m.ticket_action_accept(),
                        icon: 'icon-[material-symbols--assignment-turned-in-outline]',
                        onClick: () => setActiveModal('wf-accept'),
                      })}
                      ${wfButton({
                        key: 'travel_start',
                        label: m.ticket_action_travel_start(),
                        icon: 'icon-[material-symbols--directions-car-outline]',
                        onClick: () => setActiveModal('wf-travel-start'),
                      })}
                      ${wfButton({
                        key: 'intervention_start',
                        label: m.ticket_action_intervention_start(),
                        icon: 'icon-[material-symbols--play-circle-outline]',
                        onClick: () => setActiveModal('wf-intervention-start'),
                      })}
                      ${wfButton({
                        key: 'intervention_end',
                        label: m.ticket_action_intervention_end(),
                        icon: 'icon-[material-symbols--check-circle-outline]',
                        onClick: () => setActiveModal('wf-intervention-end'),
                      })}
                    </div>
                  </div>
                </div>

                <!-- Parts handling actions — hidden product-wide (PARTS_ACTIONS_ENABLED). -->
                ${PARTS_ACTIONS_ENABLED
                  ? html`
                <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm">
                  <div class="px-4 pt-4 pb-2 flex items-center gap-2">
                    <span class="icon-[material-symbols--inventory-2-outline] text-lg text-primary"></span>
                    <h3 class="font-semibold flex-1">${m.ticket_section_parts_title()}</h3>
                  </div>
                  <div class="px-4 pb-4 flex flex-col gap-3">
                    <p class="text-xs text-base-content/60">${m.ticket_section_parts_help()}</p>
                    <div class="flex flex-wrap gap-2">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline gap-1"
                        ?disabled=${actionBusy != null || isTicketClosed || isPendingAction}
                        @click=${() =>
                          runAction(
                            'parts_claimed',
                            () => ticketActionPartsClaimed(ticket.number!)({ number: 1 }),
                            m.ticket_action_claim_parts_success(),
                          )}
                      >
                        <span class="icon-[material-symbols--inventory-2-outline] text-base"></span>
                        ${m.ticket_action_claim_parts()}
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline gap-1"
                        ?disabled=${actionBusy != null || isTicketClosed || isPendingAction}
                        @click=${() =>
                          runAction(
                            'parts_returned_to_pudo',
                            () => ticketActionPartsReturnedToPudo(ticket.number!)({ number: 1 }),
                            m.ticket_action_leave_parts_at_pudo_success(),
                          )}
                      >
                        <span class="icon-[material-symbols--package-2-outline] text-base"></span>
                        ${m.ticket_action_leave_parts_at_pudo()}
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline btn-warning gap-1"
                        ?disabled=${actionBusy != null || isTicketClosed || isPendingAction}
                        @click=${() =>
                          runAction(
                            'parts_not_found',
                            () => ticketActionPartsNotDispatched(ticket.number!)({ number: 1 }),
                            m.ticket_action_parts_not_found_success(),
                          )}
                      >
                        <span class="icon-[material-symbols--report-outline] text-base"></span>
                        ${m.ticket_action_parts_not_found()}
                      </button>
                    </div>
                  </div>
                </div>
                    `
                  : nothing}

                <!-- General comment — free-text handling note the FE can leave any time. -->
                <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm">
                  <div class="px-4 py-4">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline btn-block gap-1"
                      ?disabled=${actionBusy != null || isTicketClosed || isPendingAction}
                      @click=${() => {
                        setModalError(null);
                        setActiveModal('comment');
                      }}
                    >
                      <span class="icon-[material-symbols--chat-outline] text-base"></span>
                      ${m.ticket_action_comment_short()}
                    </button>
                  </div>
                </div>

                <!-- Exceptions (collapsed by default — used <5% of the time per the spec). -->
                ${(() => {
                  // Opt-in per customer: show only buttons the ticket's customer enabled; drop the
                  // whole section when none are. (Cancellation is always available and lives
                  // outside this section — see the cancel action below.)
                  const buttons: Array<{ label: string; icon: string; tone: 'default' | 'error'; modal: ActiveModal }> =
                    [
                      {
                        label: m.ticket_action_cad_short(),
                        icon: 'icon-[material-symbols--event-repeat-outline]',
                        tone: 'default',
                        modal: 'cad',
                      },
                      {
                        label: m.ticket_action_cus_short(),
                        icon: 'icon-[material-symbols--directions-walk-outline]',
                        tone: 'default',
                        modal: 'cus',
                      },
                      {
                        label: m.ticket_action_cna_short(),
                        icon: 'icon-[material-symbols--phone-disabled-outline]',
                        tone: 'default',
                        modal: 'cna',
                      },
                      {
                        label: m.ticket_action_cra_short(),
                        icon: 'icon-[material-symbols--lock-outline]',
                        tone: 'default',
                        modal: 'cra',
                      },
                      {
                        label: m.ticket_action_tpm_short(),
                        icon: 'icon-[material-symbols--handyman-outline]',
                        tone: 'default',
                        modal: 'tpm',
                      },
                    ];
                  const visible = buttons.filter(b => enabledReports.has(b.modal as string));
                  if (visible.length === 0) return nothing;
                  return html`
                    <details class="rounded-2xl border border-base-300 bg-base-100 shadow-sm group">
                      <summary class="px-4 py-4 flex items-center gap-2 cursor-pointer list-none">
                        <span class="icon-[material-symbols--report-problem-outline] text-lg text-warning"></span>
                        <h3 class="font-semibold flex-1">${m.ticket_section_exceptions_title()}</h3>
                        <span
                          class="icon-[material-symbols--expand-more] text-lg text-base-content/40 transition-transform group-open:rotate-180"
                        ></span>
                      </summary>
                      <div class="px-4 pb-4 flex flex-col gap-3">
                        <p class="text-xs text-base-content/60">${m.ticket_section_exceptions_help()}</p>
                        <div class="flex flex-wrap gap-2">${visible.map(b => exceptionButton(b))}</div>
                      </div>
                    </details>
                  `;
                })()}

                <!-- Request cancellation — always available (never opt-in), kept apart from the
                     forward workflow and the opt-in exceptions. Low-emphasis + error-toned so it's
                     discoverable but not mistapped; the modal's mandatory reason is the confirmation. -->
                ${!isTicketClosed
                  ? html`
                      <div class="pt-2 mt-1 border-t border-base-300/60">
                        <button
                          type="button"
                          class="btn btn-ghost btn-sm btn-block gap-1 font-normal text-error/80 hover:text-error hover:bg-error/10"
                          ?disabled=${actionBusy != null || isPendingAction}
                          @click=${() => {
                            setModalError(null);
                            setActiveModal('cancel');
                          }}
                        >
                          <span class="icon-[material-symbols--cancel-outline] text-base"></span>
                          ${m.ticket_action_request_cancellation_short()}
                        </button>
                      </div>
                    `
                  : nothing}
              </div>
            `;
          })()
        : nothing}

      <!-- Modals -->
      ${activeModal === 'cad' && isAssigneeFE
        ? CadModal({
            busy: actionBusy != null,
            error: modalError,
            onClose: () => {
              if (actionBusy) return;
              setActiveModal(null);
              setModalError(null);
            },
            onSubmit: submitCad,
            showSecuredSite: enabledReports.has('cra'),
          })
        : nothing}
      ${activeModal === 'cus' && isAssigneeFE
        ? CusModal({
            busy: actionBusy != null,
            error: modalError,
            onClose: () => {
              if (actionBusy) return;
              setActiveModal(null);
              setModalError(null);
            },
            onSubmit: submitCus,
            allowReschedule: enabledReports.has('cad'),
            showSecuredSite: enabledReports.has('cra'),
          })
        : nothing}
      ${activeModal === 'cna' && isAssigneeFE
        ? CnaModal({
            busy: actionBusy != null,
            error: modalError,
            onClose: () => {
              if (actionBusy) return;
              setActiveModal(null);
              setModalError(null);
            },
            onSubmit: values =>
              runModalAction(
                'cna',
                () =>
                  ticketActionAttemptedContact(
                    ticket.number!,
                    values.attempts,
                    values.result,
                    values.notes,
                    values.numbers,
                    toGmtTimestamp(values.callTime),
                  )({ number: 1 }),
                m.ticket_action_cna_success(),
              ),
          })
        : nothing}
      ${activeModal === 'cra' && isAssigneeFE
        ? CraModal({
            busy: actionBusy != null,
            error: modalError,
            onClose: () => {
              if (actionBusy) return;
              setActiveModal(null);
              setModalError(null);
            },
            onSubmit: notes =>
              runModalAction(
                'cra',
                () => ticketActionAccessNotGranted(ticket.number!, 'SECURED_SITE', notes)({ number: 1 }),
                m.ticket_action_cra_success(),
              ),
          })
        : nothing}
      ${activeModal === 'tpm' && isAssigneeFE
        ? TpmModal({
            busy: actionBusy != null,
            error: modalError,
            onClose: () => {
              if (actionBusy) return;
              setActiveModal(null);
              setModalError(null);
            },
            // TPM maps to the spec's `other_problem` action (DELL replycode TPM [E10]); the
            // mandatory notes are the whole report and go through verbatim as payload.reason.
            onSubmit: notes =>
              runModalAction(
                'tpm',
                () => ticketActionOtherProblem(ticket.number!, notes)({ number: 1 }),
                m.ticket_action_tpm_success(),
              ),
          })
        : nothing}
      ${activeModal === 'cancel' && isAssigneeFE
        ? CancellationModal({
            busy: actionBusy != null,
            error: modalError,
            onClose: () => {
              if (actionBusy) return;
              setActiveModal(null);
              setModalError(null);
            },
            onSubmit: reason =>
              runModalAction(
                'cancel',
                () => ticketActionCancellationRequested(ticket.number!, reason)({ number: 1 }),
              ),
          })
        : nothing}
      ${activeModal === 'comment' && isAssigneeFE
        ? CommentModal({
            busy: actionBusy != null,
            error: modalError,
            onClose: () => {
              if (actionBusy) return;
              setActiveModal(null);
              setModalError(null);
            },
            onSubmit: (comment, emergency) =>
              runModalAction(
                'comment',
                () => ticketActionAddComment(ticket.number!, comment, emergency)({ number: 1 }),
                m.ticket_action_comment_success(),
              ),
          })
        : nothing}
      ${activeModal === 'wf-accept' && isAssigneeFE
        ? WorkflowTimestampModal({
            busy: actionBusy != null,
            error: modalError,
            title: m.ticket_action_accept(),
            iconClass: 'icon-[material-symbols--assignment-turned-in-outline]',
            submitLabel: m.ticket_action_accept(),
            // Accepting an assignment is always logged as "now" — no backdating.
            allowBackdate: false,
            onClose: () => {
              if (actionBusy) return;
              setActiveModal(null);
              setModalError(null);
            },
            onSubmit: at =>
              runModalAction(
                'accept',
                () => ticketActionInterventionAccepted(ticket.number!, toGmtTimestamp(at))({ number: 1 }),
              ),
          })
        : nothing}
      ${activeModal === 'wf-travel-start' && isAssigneeFE
        ? TravelStartModal({
            busy: actionBusy != null,
            error: modalError,
            onClose: () => {
              if (actionBusy) return;
              setActiveModal(null);
              setModalError(null);
            },
            onSubmit: async values => {
              const ok = await runModalAction(
                'travel_start',
                () =>
                  ticketActionEnRoute(
                    ticket.number!,
                    toGmtTimestamp(values.eta),
                    toGmtTimestamp(values.at),
                  )({ number: 1 }),
              );
              // Start broadcasting live position only once en_route actually succeeded — and
              // only if the FE hasn't opted out of tracking via the geolocation-card toggle.
              if (ok && trackingAllowed) setIsTracking(true);
            },
          })
        : nothing}
      ${activeModal === 'wf-intervention-start' && isAssigneeFE
        ? WorkflowTimestampModal({
            busy: actionBusy != null,
            error: modalError,
            title: m.ticket_action_intervention_start(),
            iconClass: 'icon-[material-symbols--play-circle-outline]',
            submitLabel: m.ticket_action_intervention_start(),
            onClose: () => {
              if (actionBusy) return;
              setActiveModal(null);
              setModalError(null);
            },
            onSubmit: async at => {
              const ok = await runModalAction(
                'intervention_start',
                () => ticketActionInterventionStarted(ticket.number!, toGmtTimestamp(at))({ number: 1 }),
              );
              if (ok) stopBroadcast(); // intervention started → arrived on site, stop the live broadcast
            },
          })
        : nothing}
      ${activeModal === 'wf-intervention-end' && isAssigneeFE
        ? WorkflowTimestampModal({
            busy: actionBusy != null,
            error: modalError,
            title: m.ticket_action_intervention_end(),
            iconClass: 'icon-[material-symbols--check-circle-outline]',
            submitLabel: m.ticket_action_intervention_end(),
            onClose: () => {
              if (actionBusy) return;
              setActiveModal(null);
              setModalError(null);
            },
            onSubmit: at =>
              runModalAction(
                'intervention_end',
                () => ticketActionInterventionFinished(ticket.number!, toGmtTimestamp(at))({ number: 1 }),
              ),
          })
        : nothing}

      <!-- Customer review (Customer role + closed/cancelled ticket only). -->
      ${canReview &&
      isCustomerOfTicket &&
      (ticket.state === '3' || ticket.state === '4' || ticket.state === '7')
        ? html`
            <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm">
              <div class="px-4 pt-4 pb-2 flex items-center gap-2">
                <span class="icon-[material-symbols--reviews-outline] text-lg text-primary"></span>
                <h3 class="font-semibold">${m.ticket_review_title()}</h3>
              </div>
              <div class="px-4 pb-4 flex flex-col gap-3 text-sm">
                ${myReviewQuery.value
                  ? html`
                      <div class="alert alert-success">
                        <div class="flex flex-col gap-1">
                          <p>${m.ticket_review_thanks()}</p>
                          <p class="text-xs">
                            ${m.ticket_review_summary({
                              rating: myReviewQuery.value.rating ?? 0,
                              success: myReviewQuery.value.successfullyCompleted
                                ? m.ticket_review_yes()
                                : m.ticket_review_no(),
                            })}
                          </p>
                        </div>
                      </div>
                    `
                  : html`
                      ${reviewError ? html`<div class="alert alert-error text-sm py-2">${reviewError}</div>` : nothing}
                      <div>
                        <p class="text-base-content/70 mb-1">${m.ticket_review_rating_label()}</p>
                        <div class="rating rating-md">
                          ${[1, 2, 3, 4, 5].map(
                            n =>
                              html`<input
                                type="radio"
                                name="rev-star"
                                class="mask mask-star-2 bg-warning"
                                ?checked=${reviewRating === n}
                                @change=${() => setReviewRating(n)}
                              />`,
                          )}
                        </div>
                      </div>
                      <div>
                        <p class="text-base-content/70 mb-1">${m.ticket_review_success_question()}</p>
                        <div class="flex gap-2">
                          <button
                            type="button"
                            class="btn btn-sm ${reviewSuccess === true ? 'btn-success' : 'btn-outline'}"
                            @click=${() => setReviewSuccess(true)}
                          >
                            ${m.ticket_review_yes()}
                          </button>
                          <button
                            type="button"
                            class="btn btn-sm ${reviewSuccess === false ? 'btn-error' : 'btn-outline'}"
                            @click=${() => setReviewSuccess(false)}
                          >
                            ${m.ticket_review_no()}
                          </button>
                        </div>
                      </div>
                      <div>
                        <p class="text-base-content/70 mb-1">${m.ticket_review_comment_label()}</p>
                        <textarea
                          class="textarea textarea-bordered w-full"
                          rows="3"
                          .value=${reviewComment}
                          @input=${(e: Event) => setReviewComment((e.target as HTMLTextAreaElement).value)}
                        ></textarea>
                      </div>
                      <div>
                        <button
                          type="button"
                          class="btn btn-primary btn-sm gap-1"
                          ?disabled=${reviewBusy || reviewRating < 1 || reviewSuccess === null}
                          @click=${async () => {
                            setReviewBusy(true);
                            setReviewError(null);
                            try {
                              await submitTicketReview(
                                ticket.number!,
                                reviewRating,
                                reviewSuccess!,
                                reviewComment,
                              )({ _id: 1 });
                              setReviewSubmittedAt(Date.now());
                            } catch (err: unknown) {
                              setReviewError(errorOrFallback(err, m.ticket_review_submit_failed()));
                            } finally {
                              setReviewBusy(false);
                            }
                          }}
                        >
                          ${reviewBusy
                            ? html`<span class="loading loading-spinner loading-xs"></span>`
                            : html`<span class="icon-[material-symbols--send-outline] text-base"></span>`}
                          ${m.ticket_review_submit()}
                        </button>
                      </div>
                    `}
              </div>
            </div>
          `
        : nothing}
    </div>
  `;
});
